import React from 'react';
import {
  Card,
  Form,
  Button,
  Input,
  Row,
  Col,
  Icon,
  Select,
  Tooltip,
  message as showMessage,
} from 'antd';
import moment from 'moment';

import { getData, postDownload } from '../../helper/dataService';
import API from '../../helper/api';
import { PermissionContext } from '../../permissionContext';

const readBlobAsText = (blob) => {
  return new Promise((resolve) => {
    if (!blob || !(blob instanceof Blob)) {
      return resolve(null);
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsText(blob);
  });
};

class Certificate extends React.Component {
  static contextType = PermissionContext;

  state = {
    searchQuery: '',
    assetFetching: false,
    selectedAsset: null,
    multipleAssets: [],
    submitted: false,
  };

  componentDidMount() {
    const params = new URLSearchParams(this.props.location ? this.props.location.search : '');
    const query = params.get('search');
    if (query) {
      this.setState({ searchQuery: query });
      this.handleSearch(query);
    }
  }

  // * SEARCH ASSET CLICK TRIGGER (/admin/assets/certificate?search=...)
  handleSearch = (value) => {
    const query = typeof value === 'string' ? value : this.state.searchQuery;
    const term = (query || '').trim();
    if (!term) {
      showMessage.warning('Please enter a search term');
      return;
    }

    this.setState({ assetFetching: true, multipleAssets: [] });

    getData(API.asset.certificate(encodeURIComponent(term)))
      .then(({ data }) => {
        this.setState({ assetFetching: false });
        if (data && (data.status === 'success' || !data.status)) {
          const resData = data.data !== undefined ? data.data : data;
          if (Array.isArray(resData)) {
            if (resData.length === 1) {
              this.handleSelectAsset(resData[0]);
            } else if (resData.length > 1) {
              this.setState({ multipleAssets: resData });
              showMessage.info(`${resData.length} assets found. Please select one.`);
            } else {
              showMessage.warning('No asset found with this search term.');
            }
          } else if (resData && typeof resData === 'object') {
            this.handleSelectAsset(resData);
          } else {
            showMessage.warning('No asset found with this search term.');
          }
        } else {
          showMessage.error(data.display || data.message || 'No asset found');
        }
      })
      .catch((err) => {
        this.setState({ assetFetching: false });
        const errorMsg =
          (err.response &&
            err.response.data &&
            (err.response.data.display || err.response.data.message)) ||
          'No asset found with this search term.';
        showMessage.error(errorMsg);
      });
  };

  // * SELECT ASSET & AUTOFILL FIELDS
  handleSelectAsset = (asset) => {
    if (!asset) return;

    const ownerName =
      asset.owner_name ||
      asset.owner ||
      (asset.organization && asset.organization.name) ||
      asset.organization_name ||
      asset.name ||
      '';

    // Show asset_name in Registration number field
    const registrationNo =
      asset.asset_name ||
      asset.registration_no ||
      asset.asset_plate_number ||
      asset.plate_number ||
      asset.reg_no ||
      '';

    const vehicleType = (
      asset.vehicle_type ||
      asset.asset_type ||
      ''
    ).toUpperCase();

    const chassisNo =
      asset.chassis_no !== undefined && asset.chassis_no !== null
        ? String(asset.chassis_no)
        : '';

    const validTillRaw =
      asset.vaild_till ||
      asset.valid_till ||
      asset.valid_until;

    const installationDate = asset.installation_date
      ? moment(asset.installation_date, [
          'YYYY-MM-DD HH:mm:ss.SSSSSS',
          'YYYY-MM-DD HH:mm:ss',
          'YYYY-MM-DD',
          'DD/MM/YYYY',
          'DD-MM-YYYY',
          moment.ISO_8601,
        ])
      : asset.created_on
      ? moment(asset.created_on.$date || asset.created_on)
      : moment();

    const validUntil = validTillRaw
      ? moment(validTillRaw, [
          'YYYY-MM-DD HH:mm:ss.SSSSSS',
          'YYYY-MM-DD HH:mm:ss',
          'YYYY-MM-DD',
          'DD/MM/YYYY',
          'DD-MM-YYYY',
          moment.ISO_8601,
        ])
      : moment(installationDate).add(1, 'year').subtract(1, 'day');

    this.setState(
      {
        selectedAsset: asset,
        multipleAssets: [],
      },
      () => {
        this.props.form.setFieldsValue({
          owner_name: ownerName,
          registration_no: registrationNo,
          vehicle_type: vehicleType,
          chassis_no: chassisNo,
          installation_date: installationDate
            ? installationDate.format('DD/MM/YYYY')
            : '',
          valid_until: validUntil ? validUntil.format('DD/MM/YYYY') : '',
        });
      }
    );

    showMessage.success(`Asset "${registrationNo || ownerName}" loaded.`);
  };

  clearAsset = () => {
    this.props.form.resetFields();
    this.setState({
      selectedAsset: null,
      multipleAssets: [],
      searchQuery: '',
    });
  };

  handleSubmit = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const { checkSubSectionPermission } = this.context || {};
    if (
      checkSubSectionPermission &&
      !checkSubSectionPermission('certificate', 'download')
    ) {
      showMessage.error('You do not have permission to download certificates');
      return;
    }
    this.props.form.validateFieldsAndScroll(async (err, values) => {
      if (!err) {
        this.setState({ submitted: true });

        const formatPayloadDate = (val) => {
          if (!val) return '';
          const m = moment(val, ['DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601]);
          return m.isValid() ? m.format('YYYY-MM-DD') : val;
        };

        const payload = {
          owner_name: values.owner_name,
          registration_no: values.registration_no,
          vehicle_type: values.vehicle_type,
          chassis_no: values.chassis_no,
          installation_date: formatPayloadDate(values.installation_date),
          valid_till: formatPayloadDate(values.valid_until),
        };

        showMessage.loading({
          content: 'Generating and downloading certificate PDF...',
          key: 'cert_download',
          duration: 0,
        });

        try {
          const response = await postDownload(
            API.asset.certificateGenerate,
            payload
          );

          const contentType =
            (response.headers && response.headers['content-type']) || '';

          // Check if response is JSON (either header or parsed blob)
          if (
            contentType.includes('application/json') ||
            (response.data &&
              response.data.type &&
              response.data.type.includes('application/json'))
          ) {
            const rawText = await readBlobAsText(response.data);
            const json = rawText ? JSON.parse(rawText) : {};

            if (json.status === 'success' || !json.status) {
              const fileUrl =
                (json.data &&
                  (json.data.url ||
                    (typeof json.data === 'string' &&
                    json.data.startsWith('http')
                      ? json.data
                      : null))) ||
                json.url;

              if (fileUrl) {
                window.open(fileUrl, '_blank');
                showMessage.success({
                  content: `Certificate downloaded for ${values.registration_no}!`,
                  key: 'cert_download',
                });
              } else {
                showMessage.success({
                  content:
                    json.display ||
                    json.message ||
                    'Certificate generated successfully!',
                  key: 'cert_download',
                });
              }
            } else {
              showMessage.error({
                content:
                  json.display ||
                  json.message ||
                  'Failed to generate certificate',
                key: 'cert_download',
              });
            }
          } else {
            // Binary PDF stream -> Trigger direct browser download
            const blob = new Blob([response.data], {
              type: 'application/pdf',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const filename = `Certificate_${(
              values.registration_no || 'Vehicle'
            ).replace(/\s+/g, '_')}.pdf`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            showMessage.success({
              content: `Certificate downloaded for ${values.registration_no}!`,
              key: 'cert_download',
            });
          }
        } catch (error) {
          let errorMsg = 'Failed to generate certificate';
          if (error.response && error.response.data) {
            if (error.response.data instanceof Blob) {
              const text = await readBlobAsText(error.response.data);
              try {
                const parsed = JSON.parse(text);
                errorMsg = parsed.display || parsed.message || errorMsg;
              } catch (e) {}
            } else if (typeof error.response.data === 'object') {
              errorMsg =
                error.response.data.display ||
                error.response.data.message ||
                errorMsg;
            }
          }
          showMessage.error({
            content: errorMsg,
            key: 'cert_download',
          });
        } finally {
          this.setState({ submitted: false });
        }
      }
    });
  };

  render() {
    const { getFieldDecorator } = this.props.form;
    const { checkSubSectionPermission } = this.context || {};
    const canDownload = checkSubSectionPermission
      ? checkSubSectionPermission('certificate', 'download')
      : true;
    const {
      searchQuery,
      assetFetching,
      selectedAsset,
      multipleAssets,
      submitted,
    } = this.state;

    const formItemLayout = {
      labelCol: {
        xs: { span: 24 },
        sm: { span: 8 },
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 16 },
      },
    };

    const isAssetSelected = Boolean(selectedAsset);

    return (
      <Row type='flex' justify='space-around' align='middle'>
        <Col xs={24} sm={18}>
          <Card
            title={
              <span className='title'>
                <Icon type='safety-certificate' />
                {' Certificate'}
              </span>
            }
          >
            {/* Search Asset Form */}
            <Form
              layout='vertical'
              onSubmit={(e) => {
                e.preventDefault();
                this.handleSearch();
              }}
            >
              {/* Search Asset Field with Search Button */}
              <Form.Item
                label='Search Asset'
                extra={
                  isAssetSelected ? (
                    <div style={{ marginTop: 8 }}>
                      <Button.Group size='small'>
                        <Button>
                          {selectedAsset.owner_name || selectedAsset.name}{' '}
                          {selectedAsset.asset_name ||
                          selectedAsset.registration_no ||
                          selectedAsset.asset_plate_number
                            ? `[${selectedAsset.asset_name || selectedAsset.registration_no || selectedAsset.asset_plate_number}]`
                            : ''}
                        </Button>
                        <Tooltip title='Clear / Search Another'>
                          <Button icon='delete' onClick={this.clearAsset} />
                        </Tooltip>
                      </Button.Group>
                    </div>
                  ) : null
                }
                {...formItemLayout}
              >
                <Input.Search
                  placeholder='Search by Registration No, Name, or IMEI...'
                  enterButton='Search'
                  value={searchQuery}
                  onChange={(e) => this.setState({ searchQuery: e.target.value })}
                  onSearch={this.handleSearch}
                  onPressEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleSearch();
                  }}
                  loading={assetFetching}
                  disabled={assetFetching}
                />
              </Form.Item>

              {/* If multiple assets returned, display selection dropdown */}
              {multipleAssets && multipleAssets.length > 1 && (
                <Form.Item label='Select Vehicle' {...formItemLayout}>
                  <Select
                    placeholder='Multiple vehicles found, please select one'
                    onChange={(idx) => this.handleSelectAsset(multipleAssets[idx])}
                  >
                    {multipleAssets.map((item, idx) => (
                      <Select.Option key={idx} value={idx}>
                        {item.registration_no || item.asset_plate_number || item.name} -{' '}
                        {item.owner_name || item.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Form>

            {/* After search and select, show the fields from the image */}
            {isAssetSelected && (
              <Form onSubmit={this.handleSubmit} layout='vertical'>
                {/* Owner Name * */}
                <Form.Item label='Owner Name' {...formItemLayout}>
                  {getFieldDecorator('owner_name', {
                    rules: [
                      {
                        required: true,
                        message: 'Please enter owner name',
                      },
                    ],
                  })(<Input placeholder='MD. RAHIM UDDIN' />)}
                </Form.Item>

                {/* Registration No * */}
                <Form.Item label='Registration No' {...formItemLayout}>
                  {getFieldDecorator('registration_no', {
                    rules: [
                      {
                        required: true,
                        message: 'Please enter registration no',
                      },
                    ],
                  })(<Input placeholder='DHAKA METRO-GA 12-3456' />)}
                </Form.Item>

                {/* Vehicle Type * */}
                <Form.Item label='Vehicle Type' {...formItemLayout}>
                  {getFieldDecorator('vehicle_type', {
                    rules: [
                      {
                        required: true,
                        message: 'Please enter vehicle type',
                      },
                    ],
                  })(<Input placeholder='CAR' />)}
                </Form.Item>

                {/* Chassis No */}
                <Form.Item label='Chassis No' {...formItemLayout}>
                  {getFieldDecorator('chassis_no', {
                    rules: [],
                  })(
                    <Input
                      placeholder='Chassis No'
                      readOnly
                      style={{
                        color: '#262626',
                        backgroundColor: '#fafafa',
                        cursor: 'default',
                      }}
                    />
                  )}
                </Form.Item>

                {/* Installation Date * */}
                <Form.Item label='Installation Date' {...formItemLayout}>
                  {getFieldDecorator('installation_date', {
                    rules: [
                      {
                        required: true,
                        message: 'Please enter installation date',
                      },
                    ],
                  })(
                    <Input
                      placeholder='DD/MM/YYYY'
                      readOnly
                      style={{
                        color: '#262626',
                        backgroundColor: '#fafafa',
                        cursor: 'default',
                      }}
                    />
                  )}
                </Form.Item>

                {/* Valid Until * */}
                <Form.Item label='Valid Until' {...formItemLayout}>
                  {getFieldDecorator('valid_until', {
                    rules: [
                      {
                        required: true,
                        message: 'Please enter valid until date',
                      },
                    ],
                  })(
                    <Input
                      placeholder='DD/MM/YYYY'
                      readOnly
                      style={{
                        color: '#262626',
                        backgroundColor: '#fafafa',
                        cursor: 'default',
                      }}
                    />
                  )}
                </Form.Item>

                {/* Red CTA Button & Status Note */}
                {canDownload && (
                  <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Button
                      type='danger'
                      size='large'
                      htmlType='submit'
                      loading={submitted}
                      style={{
                        backgroundColor: '#e5252a',
                        borderColor: '#e5252a',
                        color: '#ffffff',
                        fontWeight: 600,
                      }}
                    >
                      Generate & Download PDF
                    </Button>
                    <div
                      style={{
                        marginTop: 10,
                        color: '#6b7280',
                        fontSize: 13,
                      }}
                    >
                      Status is computed automatically from Valid Until.
                    </div>
                  </div>
                )}
              </Form>
            )}
          </Card>
        </Col>
      </Row>
    );
  }
}

export default Form.create()(Certificate);
